import 'CssFolder/components/multi-select.scss';
import { Icon, Icons, Intent, ListItem, SelectList } from "construct-ui";
import languageModel from "../../models/language.model";

/**
 * Multi Select Component
 * 
 * Dropdown input component for multi select. 
 * Supports key or object inputs.
 * 
 * @author Darrus
 */
export default function MultiSelectComponent() {
  const DEFAULT_ONSELECT = () => { console.log('MultiSelectComponent: onSelect()') };
  const LIST_COMPONENT_X_PADDING = '2rem';
  const TITLE_SEPARATOR = ', ';

  // DO NOT MODIFY MANUALLY - Rendered width of this component
  let componentWidth;

  // COMMON FUNCTIONS
  const getItemList = (items, selectedItems, itemKey, itemValue) => {
    let result = [];

    if (items && items.length > 0) {
      result = structuredClone(items);
    }

    if (result.length > 0) {
      result.unshift(
        {
          [itemKey]: "",
          [itemValue]: _.isEmpty(selectedItems)
            ? languageModel.getText("LT00000077", "Select all")
            : languageModel.getText("LT00000285", "Clear selection")
        }
      );
    }
    return result;
  }

  const itemPredicate = (filterableByKey, itemKey, itemValue) => (query, item) => {
    const foundInValue = item[itemValue].trim().toLowerCase().includes(query.trim().toLowerCase());

    if (filterableByKey) {
      const foundInKey = item[itemKey].trim().toLowerCase().includes(query.trim().toLowerCase());
      return foundInKey || foundInValue;
    }

    return foundInValue;
  }

  // OBJECT FUNCTIONS
  const renderItemObject = (selectedItems, itemKey, itemValue, itemIcons, showKeyInLabel) => (item, index) => {
    return m(ListItem, {
      allowOnContentClick: true,
      label: m('span.text-wrap',
        itemIcons.length > 0 ? itemIcons[index] : null,
        showKeyInLabel && item[itemKey] ? item[itemValue] + ' - ' + item[itemKey] : item[itemValue]
      ),
      selected: selectedItems.find(i => i[itemKey] === item[itemKey])
    });
  }

  const handleSelectObject = (items, selectedItems, itemKey, itemValue, onSelect) => (item, e, index) => {
    let result = structuredClone(selectedItems);

    if (item[itemValue] === languageModel.getText("LT00000077", "Select all")) {
      result = structuredClone(items);
    } else if (item[itemValue] === languageModel.getText("LT00000285", "Clear selection")) {
      result = [];
    } else {
      let index = result.findIndex((element) => item[itemKey] === element[itemKey]);

      if (index < 0) {
        result.push(item);
      } else {
        result.splice(index, 1);
      }
    }

    onSelect(result, item, e, index);
  }

  const renderLabelObject = (selectedItems, items, _, itemValue) => {
    if (selectedItems?.length > 0) {
      if (selectedItems.length === items.length) {
        return languageModel.getText("LT00000286", "All items selected") + ` (${selectedItems.length})`;
      } else if (selectedItems.length <= 3) {
        return selectedItems.map(item => item[itemValue]).join(', ');
      } else {
        return selectedItems.length + ' ' + languageModel.getText("LT00000308", "items selected");
      }
    }

    return '';
  };

  const renderTitleObject = (selectedItems, items, _, itemValue) => {
    if (selectedItems?.length > 0) {
      return selectedItems.map(item => item[itemValue]).join(TITLE_SEPARATOR);
    }

    return '';
  };


  // KEY FUNCTIONS
  const renderItemKey = (selectedItems, itemKey, itemValue, itemIcons, showKeyInLabel) => (item, index) => {
    return m(ListItem, {
      allowOnContentClick: true,
      label: m('span.text-wrap',
        itemIcons.length > 0 ? itemIcons[index] : null,
        showKeyInLabel && item[itemKey] ? [item[itemValue] + ' - ' + item[itemKey]] : item[itemValue]),
      selected: selectedItems.find(i => i === item[itemKey])
    });
  }

  const handleSelectKey = (items, selectedItems, itemKey, itemValue, onSelect) => (item, e, index) => {
    let result = structuredClone(selectedItems);

    if (item[itemValue] === languageModel.getText("LT00000077", "Select all")) {
      result = items.map((i) => i[itemKey]);
    } else if (item[itemValue] === languageModel.getText("LT00000285", "Clear selection")) {
      result = [];
    } else {
      let index = result.findIndex((element) => item[itemKey] === element);

      if (index < 0) {
        result.push(item[itemKey]);
      } else {
        result.splice(index, 1);
      }
    }

    onSelect(result, item[itemKey], e, index);
  }

  const renderLabelKey = (selectedItems, items, itemKey, itemValue) => {
    if (selectedItems?.length > 0) {
      if (selectedItems.length === items.length) {
        return languageModel.getText("LT00000286", "All items selected") + ` (${selectedItems.length})`;
      } else if (selectedItems.length <= 3) {
        return items
          .filter(item => selectedItems.includes(item[itemKey]))
          .map(item => item[itemValue])
          .join(', ');
      } else {
        return `${selectedItems.length} ${languageModel.getText("LT00000308", "items selected")}`;
      }
    }

    return '';
  };

  const renderTitleKey = (selectedItems, items, itemKey, itemValue) => {
    if (selectedItems?.length > 0) {
      return items
        .filter(item => selectedItems.includes(item[itemKey]))
        .map(item => item[itemValue])
        .join(TITLE_SEPARATOR);
    }

    return '';
  };

  const handleSelectFunctions = [handleSelectObject, handleSelectKey];
  const renderItemFunctions = [renderItemObject, renderItemKey];
  const renderLabelFunctions = [renderLabelObject, renderLabelKey];
  const renderTitleFunctions = [renderTitleObject, renderTitleKey];

  const updateComponentWidth = (parentWidth) => {
    if (!parentWidth) {
      componentWidth = undefined;
      return;
    }

    if (componentWidth != parentWidth) {
      componentWidth = parentWidth;
      m.redraw();
    }
  }

  const getListCSSStyle = () => {
    return {
      // make sure the dropdown list has the same width as the trigger component
      width: componentWidth ? `calc(${componentWidth}px - ${LIST_COMPONENT_X_PADDING})` : undefined
    }
  }

  return {
    onupdate: ({ dom }) => {
      updateComponentWidth(dom.offsetWidth);
    },
    view: ({
      attrs: {
        activeIndex,
        class: _class,
        disabled = false,
        filterable = true,
        filterableByKey = false,
        showKeyInLabel = false,
        id = null,
        itemIcons = [],
        itemKey = 'code',
        items = [],
        itemValue = 'name',
        keyOnly = false,
        onSelect = DEFAULT_ONSELECT,
        required = false,
        selectedItems = [],
        style = '',
        fetchData = null
      }
    }) => {
      return m(SelectList, {
        ...activeIndex != undefined && { activeIndex: activeIndex },
        filterable: items?.length > 0 ? filterable : false,
        closeOnSelect: false,
        items: !disabled && getItemList(items, selectedItems, itemKey, itemValue),
        itemRender: renderItemFunctions[+keyOnly](selectedItems, itemKey, itemValue, itemIcons, showKeyInLabel),
        itemPredicate: itemPredicate(filterableByKey, itemKey, itemValue),
        listAttrs: {
          style: getListCSSStyle()
        },
        onSelect: !disabled && handleSelectFunctions[+keyOnly](items, selectedItems, itemKey, itemValue, onSelect),
        popoverAttrs: {
          hasArrow: false
        },
        style,
        trigger: m('.d-flex.justify-content-between.align-items-center.form-control.form-control-bg.select-list.cursor-pointer',
          m('input.flex-fill.select-list.text-start.p-0.cursor-pointer', {
            autocomplete: 'off',
            id,
            class: (_class ? _class : ''),
            type: 'text',
            placeholder: (items && items.length > 0)
              ? languageModel.getText('LT00000201', 'Please select')
              : languageModel.getText('LT00000363', 'No items available'),
            style: {
              width: 'inherit',
              'overflow-x': 'hidden',
              'text-overflow': 'ellipsis',
              border: 'none',
              background: 'transparent'
            },
            onclick: (e) => {
              fetchData?.();
            },
            oninput: (e) => {
              e.preventDefault();
              e.stopPropagation();
            },
            value: renderLabelFunctions[+keyOnly](selectedItems, items, itemKey, itemValue),
            title: renderTitleFunctions[+keyOnly](selectedItems, items, itemKey, itemValue),
            disabled,
            required
          }),
          m(Icon,
            {
              name: Icons.CHEVRON_DOWN,
              intent: Intent.PRIMARY
            }
          )
        )
      })
    }
  }
}